// src/components/auth/PrivateRoute.js

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../firebase/useAuth';
import MainNavbar from '../MainNavbar';
import { ArrowPathIcon } from '@heroicons/react/24/solid';
import { OptimizedImage } from '../../utils/imageManager';

const PrivateRoute = ({ children }) => {
  const { userId, userLoading, profileInitError, appCheckBlocked, dataAccessError } = useAuth();
  const location = useLocation();

  if (userLoading) {
    // Show a full page layout with the spinner to prevent layout shifts
    return (
      <div className="flex flex-col min-h-screen bg-background-primary">
        <MainNavbar />
      </div>
    );
  }

  if (!userId) {
    // If the user is not logged in, redirect them to the login page.
    return <Navigate to="/user/login" state={{ from: location }} replace />;
  }

  if (profileInitError || dataAccessError) {
    // Profile recovery or data access failed — show an actionable error
    // instead of pages waiting forever on userData. When the App Check
    // probe also failed, the cause is almost certainly client-side blocking
    // of the bot check, so lead with that.
    const heading = appCheckBlocked ? 'Connection Blocked' : 'Profile Couldn’t Load';
    const headingJa = appCheckBlocked ? '接続がブロックされました' : 'プロフィールを読み込めませんでした';
    const message = appCheckBlocked
      ? 'A VPN or privacy tool is likely blocking our bot check (reCAPTCHA), so your data can’t load. Allow it for this site or try another network, then refresh. Contact support if it persists.'
      : 'We couldn’t load or create your profile data. Please refresh the page — if the issue persists, contact support.';
    return (
      <div className="flex flex-col min-h-screen bg-background-primary">
        <MainNavbar />
        <div className="flex-grow flex items-center justify-center px-4 py-12 relative overflow-hidden">
          <div className="relative max-w-lg w-full text-center animate-slide-in-center">
            {/* Image with glow, matching NotFoundPage */}
            <div className="relative mb-8">
              <div
                className="absolute inset-0 rounded-full blur-3xl opacity-40"
                style={{
                  background: 'radial-gradient(circle, var(--color-primary), var(--color-secondary), transparent 70%)',
                  transform: 'scale(1.2)',
                }}
              />
              <OptimizedImage
                imageKey={appCheckBlocked ? 'CONNECTION_BLOCKED' : 'ACTION_INCORRECT'}
                className={`relative ${appCheckBlocked ? 'w-80 sm:w-[32rem]' : 'w-48 sm:w-56'} h-auto mx-auto drop-shadow-2xl`}
                fallback={<span className="text-8xl">{appCheckBlocked ? '🤖' : '😔'}</span>}
              />
            </div>

            <div className="mb-6">
              <h1 className="text-3xl sm:text-4xl font-black text-content-primary mb-3 drop-shadow-sm">{heading}</h1>
              <p className="text-xl sm:text-2xl font-bold text-content-primary opacity-80 drop-shadow-sm" lang="ja">
                {headingJa}
              </p>
            </div>

            <p className="text-lg text-content-secondary opacity-90 mb-10 drop-shadow-sm">{message}</p>

            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center px-8 py-4 rounded-2xl font-bold text-white transition-all duration-300 hover:shadow-lg hover:-translate-y-1 active:scale-95"
              style={{ backgroundColor: 'var(--color-primary)' }}
            >
              <ArrowPathIcon className="h-6 w-6 mr-3 drop-shadow-sm" />
              <span className="text-lg drop-shadow-sm">Refresh</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If the user is logged in, render the requested component.
  return children;
};

export default PrivateRoute;
